/* eslint-disable max-statements */
/* eslint-disable complexity */
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocale } from '~/components/Intl';
import { InjectedIntl } from 'react-intl';

//@ts-ignore
import { reload } from '@mindtickle/utils';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUserAuth } from '~/components/Auth';
import { APP_NAME } from '~/config/app.constants';
import { withTranslationsInternal } from '~/components/Intl';
import { fetchLoginStatus } from './api';

import { showLogoutAlert, getCallAIRedirectionUrl } from './helpers';
import { getApplicationAccessLink, APPLICATIONS } from '~/components/ApplicationUrls';

const StyledIframe = styled.iframe`
  height: calc(100vh - var(--headerOffset));
  width: 100%;
  overflow-y: auto;
  border: none;
`;

export const CallAI: React.FC<{
  intl?: InjectedIntl;
  toUseMTAuth: boolean;
  callAIBaseUrl: string;
}> = ({ intl, toUseMTAuth, callAIBaseUrl }) => {
  const { currentLanguage } = useLocale();
  const [redirectionUrl, setRedirectionUrl] = useState('');
  const { sessionId = '', id: userId = '' } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onPageChanged = useCallback(
    (event: { origin: string; data: any }) => {
      if (event.origin.indexOf('.callai.') >= 0 || event.origin.indexOf('local') >= 0) {
        if (event.data === 'HOME') {
          reload(getApplicationAccessLink(APPLICATIONS.LEARNER_READINESS_DASHBOARD), {
            replace: true,
          });
        }
        if (event.data.messageData && event.data.messageData === 'SESSION_TIMED_OUT') {
          return window.location.reload();
        }

        const searchParam = event.data.search
          ? `?search=${encodeURIComponent(event.data.search?.replace('?', ''))}`
          : '';
        // notify and reload options to prevent retriggering ui-view
        if (event.data.pathname) {
          navigate(
            `${getApplicationAccessLink(APPLICATIONS.LEARNER_CALL_AI)}${
              event.data.pathname
            }${searchParam}`
          );
        }
      }
    },
    [navigate, userId]
  );

  useEffect(() => {
    window.addEventListener('message', onPageChanged);
    return () => window.removeEventListener('message', onPageChanged);
  }, [onPageChanged]);

  useEffect(() => {
    fetchLoginStatus(sessionId)
      .then(loggedIn => {
        if (loggedIn) {
          // In case the feature flag is turned on -
          // base url should be the the call ai url
          // else it should be the default flow.
          const baseRedirectionUrl = toUseMTAuth
            ? `${callAIBaseUrl}?embed=true`
            : getCallAIRedirectionUrl(currentLanguage);
          const parentUrlSubroute = location.pathname?.split(
            getApplicationAccessLink(APPLICATIONS.LEARNER_CALL_AI)
          )[1];
          let parentUrlSearchParams = location.search;
          if (parentUrlSearchParams) {
            parentUrlSearchParams = `${decodeURIComponent(
              parentUrlSearchParams.replace('?search=', '')
            )}`;
          }
          let defaultSearch = `_requestStartTime=${window.performance.timing.connectStart}&_MTPageLoad=true`;
          if (parentUrlSearchParams && !parentUrlSearchParams.includes('_selfRouting')) {
            defaultSearch = `${parentUrlSearchParams}&${defaultSearch}`;
          } else if (parentUrlSearchParams) {
            defaultSearch = parentUrlSearchParams.replace('_selfRouting=true', '');
          }
          setRedirectionUrl(
            `${baseRedirectionUrl}&redirect_uri=${parentUrlSubroute}&${defaultSearch}`
          );
        } else {
          showLogoutAlert({ intl });
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(error);
        showLogoutAlert({ intl });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return redirectionUrl ? <StyledIframe allowFullScreen src={redirectionUrl} /> : null;
};

export default withTranslationsInternal<{ callAIBaseUrl: string; toUseMTAuth: boolean }>({
  appName: APP_NAME,
})(CallAI);
